import React, { useState, useRef } from "react";
import { useApi } from "../hooks/useApi.ts";
import { SearchSelect } from "./SearchSelect.tsx";
import { LoadingOverlay } from "./LoadingPanel.tsx";

interface Project {
  id: number;
  name: string;
}

interface ParsedResult {
  taskGroupId: number;
  parsed: {
    projectName: string;
    tasks: Array<{
      name: string;
      description: string;
      departments: string[];
      estimatedHoursPerDepartment: Record<string, number>;
      priority: string;
    }>;
    totalEstimatedHours: number;
  };
}

export function SOWUpload() {
  const { data: projects } = useApi<Project[]>("/api/projects");
  const [selectedProject, setSelectedProject] = useState<number>(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ParsedResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    if (!selectedProject) {
      setError("Please select a project before uploading");
      return;
    }
    setUploading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectId", String(selectedProject));

    try {
      const res = await fetch("/api/sow/upload", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Upload failed");
      } else {
        setResult(data as ParsedResult);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Upload SOW</h1>
        <p>Upload a Statement of Work to generate task structure</p>
      </div>

      <div className="card">
        <div className="form-group">
          <label>Project</label>
          <SearchSelect
            options={projects?.map((p) => ({ value: p.id, label: p.name })) || []}
            value={selectedProject}
            onChange={setSelectedProject}
            placeholder="-- Select Project --"
          />
        </div>

        <div
          className={`upload-zone ${dragging ? "dragging" : ""}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files[0];
            if (file) handleUpload(file);
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".txt,.pdf,.docx,.doc,.md"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
            }}
          />
          <>
            <p style={{ fontSize: "16px", marginBottom: "8px" }}>
              Drop your SOW file here or click to browse
            </p>
            <p style={{ color: "var(--color-text-muted)", fontSize: "14px" }}>
              Supports .txt, .pdf, .docx, .md
            </p>
          </>
        </div>

        {error && (
          <div style={{ color: "var(--color-danger)", marginTop: "12px" }}>
            {error}
          </div>
        )}
      </div>

      {uploading && (
        <LoadingOverlay message="Analyzing SOW with AI... This may take a moment." />
      )}

      {result && (
        <div className="card">
          <div className="card-header">
            <h2>Parsed: {result.parsed.projectName}</h2>
            <span className="badge badge-info">
              {result.parsed.totalEstimatedHours}h total
            </span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Departments</th>
                <th>Priority</th>
                <th>Hours</th>
              </tr>
            </thead>
            <tbody>
              {result.parsed.tasks.map((task, i) => (
                <tr key={i}>
                  <td>
                    <strong>{task.name}</strong>
                    <br />
                    <span style={{ color: "var(--color-text-muted)", fontSize: "13px" }}>
                      {task.description.slice(0, 100)}
                      {task.description.length > 100 ? "..." : ""}
                    </span>
                  </td>
                  <td>
                    {task.departments.map((d) => (
                      <span key={d} className="badge badge-neutral" style={{ marginRight: "4px" }}>
                        {d}
                      </span>
                    ))}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        task.priority === "high"
                          ? "badge-danger"
                          : task.priority === "medium"
                            ? "badge-warning"
                            : "badge-neutral"
                      }`}
                    >
                      {task.priority}
                    </span>
                  </td>
                  <td>
                    {Object.values(task.estimatedHoursPerDepartment).reduce(
                      (a, b) => a + b,
                      0
                    )}
                    h
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: "16px", textAlign: "right" }}>
            <span style={{ color: "var(--color-text-muted)", marginRight: "12px" }}>
              Task Group ID: {result.taskGroupId}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
